import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import HeroSection from '../components/HeroSection';
import BusinessCard from '../components/BusinessCard';
import TestimonialCard from '../components/TestimonialCard';
import ClientsMarquee from '../components/ClientsMarquee';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';
import { businesses, testimonials, caseStudies, insights, companyInfo } from '../mockData';

const HomePage = () => {
  // Bento arrangement — CRM large, Compliances small, Styx small, Locacres large
  const bento = [
    { id: 'lockene-crm', span: 'lg' },
    { id: 'lockene-compliances', span: 'md' },
    { id: 'styx-cafe', span: 'md' },
    { id: 'locacres', span: 'lg' }
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: companyInfo.legalName,
    alternateName: 'Lockene',
    url: 'https://lockene.info',
    logo: 'https://lockene.info/apple-touch-icon.png',
    email: companyInfo.primaryEmail,
    telephone: companyInfo.primaryPhone,
    foundingDate: companyInfo.founded,
    description: companyInfo.description,
    sameAs: [companyInfo.socials.linkedin, companyInfo.socials.facebook, companyInfo.socials.instagram],
    address: companyInfo.locations.map((l) => ({
      '@type': 'PostalAddress',
      addressLocality: l.city,
      addressCountry: l.country
    })),
    subOrganization: businesses.map((b) => ({
      '@type': 'Organization',
      name: b.name,
      description: b.description,
      url: b.website
    }))
  };

  return (
    <>
      <Seo
        title="Lockene · A Global Holding Company"
        description="Lockene operates across enterprise AI software, government compliance, sports & hospitality and premium real estate — serving 48,000+ clients across 12 countries."
        jsonLd={jsonLd}
      />

      <HeroSection />

      <ClientsMarquee />

      {/* Portfolio bento */}
      <section
        id="portfolio"
        className="section bg-brand-alabaster"
        aria-labelledby="portfolio-heading"
        data-testid="section-portfolio"
      >
        <div className="container-editorial">
          <Reveal className="max-w-3xl mb-14 md:mb-20">
            <div className="flex items-center gap-3 mb-6">
              <span className="hairline" aria-hidden />
              <span className="eyebrow">The Portfolio</span>
            </div>
            <h2 id="portfolio-heading" className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight text-brand-oxford">
              Four businesses. One operating standard.
            </h2>
            <p className="mt-6 text-lg text-brand-slate leading-relaxed">
              Each subsidiary operates independently, with its own leadership and P&amp;L —
              held to a common standard for discipline, transparency and client outcomes.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
            {bento.map(({ id, span }, i) => {
              const b = businesses.find((x) => x.id === id);
              return (
                <Reveal key={id} delay={i * 80} className={span === 'lg' ? 'lg:col-span-8' : 'lg:col-span-4'}>
                  <BusinessCard business={b} span={span} index={i} />
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Operating principles */}
      <section
        className="section bg-white border-y border-brand-hairline"
        aria-labelledby="principles-heading"
        data-testid="section-principles"
      >
        <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <span className="hairline" aria-hidden />
              <span className="eyebrow">Operating Principles</span>
            </div>
            <h2 id="principles-heading" className="font-display text-4xl md:text-5xl leading-tight text-brand-oxford">
              What is worth doing is worth doing without compromise.
            </h2>
          </div>

          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-14">
            {[
              { n: '01', t: 'Senior advisors, always', d: 'Every mandate is owned by a senior operator with sustained transaction history in the relevant discipline.' },
              { n: '02', t: 'Transparent timelines', d: 'We publish honest estimates for every engagement — and update them the moment a variable moves.' },
              { n: '03', t: 'Discipline over spectacle', d: 'Precise operational execution matters more than launch fireworks. We optimise for the eighth quarter, not the first.' },
              { n: '04', t: 'Global bench', d: 'Offices across India and North America give clients a genuine 24-hour operating envelope.' }
            ].map((p, i) => (
              <Reveal key={p.n} delay={i * 70}>
                <div>
                  <div className="font-display text-3xl text-brand-gold mb-2">{p.n}</div>
                  <h3 className="text-lg font-semibold text-brand-oxford mb-2">{p.t}</h3>
                  <p className="text-brand-slate leading-relaxed">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies preview */}
      <section
        className="section bg-brand-alabaster"
        aria-labelledby="cs-heading"
        data-testid="section-case-studies"
      >
        <div className="container-editorial">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-6">
                <span className="hairline" aria-hidden />
                <span className="eyebrow">Selected Mandates</span>
              </div>
              <h2 id="cs-heading" className="font-display text-4xl md:text-5xl leading-tight text-brand-oxford">
                Outcomes documented. Reproducibly.
              </h2>
            </div>
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brand-oxford border-b border-brand-oxford pb-1 self-start md:self-end"
              data-testid="cs-view-all"
            >
              View all case studies <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {caseStudies.map((cs, i) => (
              <Reveal key={cs.id} delay={i * 80}>
                <Link
                  to={`/case-studies/${cs.slug}`}
                  data-testid={`home-cs-${cs.slug}`}
                  className="group block bg-white border border-brand-hairline hover:shadow-lift transition-shadow duration-300"
                >
                  <div className="relative overflow-hidden">
                    <img src={cs.image} alt={cs.title} className="w-full h-64 object-cover grayscale group-hover:grayscale-0 transition-all duration-700" loading="lazy" />
                  </div>
                  <div className="p-8">
                    <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-brand-slate/80">
                      {cs.category} · <time dateTime={cs.date}>{new Date(cs.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</time>
                    </div>
                    <h3 className="font-display text-2xl md:text-[26px] leading-tight text-brand-oxford mt-3">{cs.title}</h3>
                    <p className="mt-4 text-brand-slate leading-relaxed">{cs.excerpt}</p>
                    <p className="mt-5 text-sm font-semibold text-brand-oxford">{cs.results}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Insights preview */}
      <section
        className="section bg-brand-alabaster border-t border-brand-hairline"
        aria-labelledby="ins-heading"
        data-testid="section-insights"
      >
        <div className="container-editorial">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-6">
                <span className="hairline" aria-hidden />
                <span className="eyebrow">Insights</span>
              </div>
              <h2 id="ins-heading" className="font-display text-4xl md:text-5xl leading-tight text-brand-oxford">
                Written by senior operators.
              </h2>
            </div>
            <Link
              to="/insights"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brand-oxford border-b border-brand-oxford pb-1 self-start md:self-end"
              data-testid="ins-view-all"
            >
              Read all insights <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {insights.slice(0, 3).map((p, i) => (
              <Reveal key={p.slug} delay={i * 70}>
                <Link
                  to={`/insights/${p.slug}`}
                  data-testid={`home-insight-${p.slug}`}
                  className="group block bg-white border border-brand-hairline hover:shadow-lift transition-shadow h-full"
                >
                  <div className="relative overflow-hidden">
                    <img src={p.heroImage} alt={p.title} className="w-full h-52 object-cover grayscale group-hover:grayscale-0 transition-all duration-700" loading="lazy" />
                  </div>
                  <div className="p-7">
                    <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-brand-slate/80">
                      {p.category} · {p.readMinutes} min read
                    </div>
                    <h3 className="font-display text-xl md:text-2xl leading-tight text-brand-oxford mt-3">{p.title}</h3>
                    <p className="mt-4 text-sm text-brand-slate leading-relaxed">{p.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section
        className="section bg-white border-t border-brand-hairline"
        aria-labelledby="testimonials-heading"
        data-testid="section-testimonials"
      >
        <div className="container-editorial">
          <Reveal className="max-w-3xl mb-14">
            <div className="flex items-center gap-3 mb-6">
              <span className="hairline" aria-hidden />
              <span className="eyebrow">Client Voices</span>
            </div>
            <h2 id="testimonials-heading" className="font-display text-4xl md:text-5xl leading-tight text-brand-oxford">
              The judgement that matters most is the one written by our clients.
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
            {testimonials.slice(0, 4).map((t, i) => (
              <Reveal key={t.id} delay={i * 70} className="h-full">
                <TestimonialCard testimonial={t} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-oxford text-brand-alabaster py-24 md:py-32" data-testid="section-home-cta">
        <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-10 h-px bg-brand-gold" aria-hidden />
              <span className="eyebrow text-brand-gold/90">Start with a Conversation</span>
            </div>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.05] text-brand-alabaster">
              Every engagement begins with a candid conversation — not a proposal deck.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/contact"
              data-testid="home-cta-contact"
              className="inline-flex items-center gap-3 bg-brand-alabaster text-brand-oxford px-8 py-5 text-[12px] font-semibold tracking-[0.16em] uppercase transition-colors hover:bg-brand-gold focus-ring"
            >
              Contact Lockene <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
